"use client";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { FaRegCircleUser } from "react-icons/fa6";
import { RiLogoutCircleLine } from "react-icons/ri";
import { IoChevronDown } from "react-icons/io5";
import { FALLBACK_AVATAR, onAvatarError, useAuthUser } from "./useAuthUser";

// Konto-Knopf in der Desktop-Navigation
const UserPanel = () => {
    const { user, logout } = useAuthUser();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    // Schließen bei Klick daneben oder Escape
    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    // Nicht eingeloggt: einfacher Knopf wie im Entwurf
    if (!user) {
        return (
            <Link
                href="/login"
                className="inline-flex h-11 items-center rounded-[10px] border border-ao-line-strong px-5 text-[15px] font-semibold text-ao-fg transition-colors hover:border-ao-accent"
            >
                Anmelden
            </Link>
        );
    }

    // Eingeloggt: Profilbild + Name mit Menü
    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className="flex h-11 items-center gap-2 rounded-[10px] border border-ao-line-strong pl-2 pr-4 text-[15px] font-semibold text-ao-fg transition-colors hover:border-ao-accent"
            >
                <img
                    src={user.profile_photo || FALLBACK_AVATAR}
                    onError={onAvatarError}
                    alt=""
                    className="h-7 w-7 rounded-full object-cover"
                />
                <span className="max-w-[120px] truncate">{user.firstName}</span>
                <IoChevronDown className={`text-sm text-ao-muted transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    role="menu"
                    className="absolute right-0 top-[calc(100%+8px)] w-60 overflow-hidden rounded-xl border border-ao-line bg-ao-surface py-1 shadow-2xl"
                >
                    <div className="border-b border-ao-line px-4 py-3">
                        <p className="truncate text-sm font-semibold text-ao-fg">
                            {user.firstName} {user.lastName}
                        </p>
                        <p className="truncate text-xs text-ao-muted">{user.email}</p>
                    </div>
                    <Link
                        role="menuitem"
                        href="/meinProfile"
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-ao-fg transition-colors hover:bg-ao-surface-2"
                    >
                        <FaRegCircleUser className="text-lg text-ao-accent-fg" />
                        Mein Profil
                    </Link>
                    <button
                        role="menuitem"
                        type="button"
                        onClick={() => {
                            setOpen(false);
                            logout();
                        }}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-ao-fg transition-colors hover:bg-ao-surface-2"
                    >
                        <RiLogoutCircleLine className="text-lg text-ao-accent-fg" />
                        Abmelden
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserPanel;
